
import React, { useEffect, useRef } from 'react';
import { isIOSDevice, isPwaMode } from "@/utils/pwaUtils";
import TouchDebugger from './TouchDebugger';
import { logger } from "@/utils/logger";

/**
 * Helper component for iOS PWA - handles special CSS classes and ensures proper viewport initialization
 */
export const IOSPwaHelper = () => {
  const isIOS = isIOSDevice();
  const isPwa = isPwaMode();
  const isIOSPwa = isIOS && isPwa;
  const initAttempts = useRef(0);
  const maxAttempts = 3;

  // Apply iOS PWA classes immediately on component mount
  useEffect(() => {
    if (!isIOSPwa) return;

    // Add iOS PWA marker class to html element for styling
    document.documentElement.classList.add('ios-pwa');

    // Ensure viewport is properly initialized
    const initializeViewport = () => {
      if (initAttempts.current >= maxAttempts) return;

      try {
        // Force layout recalculation
        document.body.style.minHeight = '100vh';
        setTimeout(() => {
          document.body.style.minHeight = '-webkit-fill-available';
        }, 50);

        // Force browser to recalculate viewport
        window.scrollTo(0, 0);

        logger.info(`[IOSPwaHelper] Initialized viewport (attempt ${initAttempts.current + 1})`);
        initAttempts.current++;
      } catch (e) {
        logger.error('Error initializing viewport:', e);
      }
    };

    // Initial setup
    initializeViewport();

    // Additional initialization after a delay to ensure everything is loaded
    const delayedInit = setTimeout(initializeViewport, 300);

    // Listen for orientation changes to reinitialize
    const handleOrientationChange = () => {
      setTimeout(initializeViewport, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    logger.info(`[IOSPwaHelper] Added ios-pwa class for proper styling`);

    return () => {
      document.documentElement.classList.remove('ios-pwa');
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
      clearTimeout(delayedInit);
    };
  }, [isIOSPwa]);

  // Show debug only if explicitly enabled via URL param (disabled by default)
  const isDebugEnabled = window.location.search.includes('debug=true') ||
    window.location.search.includes('touch-debug=true');

  if (!isIOSPwa) {
    return null;
  }

  return isDebugEnabled ? <TouchDebugger /> : null;
};

export default IOSPwaHelper;
